// Fatia S5b: sino com notificações in-app (contador com atualização a cada 60 s e as 10 últimas).
import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Bell, CheckCheck } from 'lucide-react'
import type { NotificationDto } from '@impulso/shared/api/notifications'
import { errorMessage } from '../../lib/api'
import { Loading } from '../../ui'
import { useMarkAllRead, useMarkRead, useNotifications, useUnreadCount } from './api'
import { NotificationItem } from './NotificationItem'

export default function NotificationsBell() {
  const [open, setOpen] = useState(false)
  const wrap = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const count = useUnreadCount()
  const list = useNotifications({ limit: 10, enabled: open })
  const markRead = useMarkRead()
  const markAll = useMarkAllRead()
  const unread = count.data?.count ?? 0

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => { if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false) }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey) }
  }, [open])

  const openItem = (n: NotificationDto) => {
    if (!n.read) markRead.mutate(n.id)
    setOpen(false)
    if (n.link?.startsWith('/')) navigate(n.link)
  }

  const label = unread > 0 ? `Notificações: ${unread} não lida${unread === 1 ? '' : 's'}` : 'Notificações'
  return (
    <div ref={wrap} style={{ position: 'relative' }}>
      <button type="button" className="icon-btn" aria-label={label} aria-expanded={open} aria-haspopup="dialog" onClick={() => setOpen((v) => !v)}>
        <Bell size={18} />
        {unread > 0 && (
          <span
            aria-hidden
            style={{
              position: 'absolute', top: -5, right: -5, minWidth: 18, height: 18, padding: '0 5px', borderRadius: 999, background: 'var(--red)',
              color: 'white', fontSize: 12, fontWeight: 700, display: 'grid', placeItems: 'center', border: '2px solid white', lineHeight: 1,
            }}
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>
      {open && (
        <div
          role="dialog"
          aria-label="Notificações"
          className="card flush"
          style={{ position: 'fixed', top: 60, right: 12, width: 'min(380px, calc(100vw - 24px))', zIndex: 45, boxShadow: 'var(--shadow-lg)', display: 'flex', flexDirection: 'column', maxHeight: 'min(560px, calc(100vh - 80px))' }}
        >
          <div className="row between" style={{ padding: '12px 14px', borderBottom: '1px solid var(--border)' }}>
            <h3>Notificações</h3>
            {unread > 0 && (
              <button type="button" className="btn ghost sm" onClick={() => markAll.mutate()} disabled={markAll.isPending}>
                <CheckCheck size={15} /> Marcar todas como lidas
              </button>
            )}
          </div>
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {list.isLoading && <div style={{ padding: 14 }}><Loading lines={3} /></div>}
            {list.error && <p className="small" role="alert" style={{ padding: 14, color: 'var(--red)' }}>{errorMessage(list.error)}</p>}
            {list.data && list.data.items.length === 0 && (
              <div className="empty" style={{ padding: '28px 16px' }}>
                <Bell size={22} />
                <p className="mt-8">Nenhuma notificação por aqui. Avisamos quando algo importante acontecer.</p>
              </div>
            )}
            {list.data?.items.map((n) => <NotificationItem key={n.id} n={n} onOpen={openItem} compact />)}
          </div>
          <Link to="/app/notificacoes" className="strong" onClick={() => setOpen(false)} style={{ padding: '12px 14px', borderTop: '1px solid var(--border)', color: 'var(--primary)', textAlign: 'center' }}>
            Ver todas as notificações
          </Link>
        </div>
      )}
    </div>
  )
}
